import React, { useState, useEffect } from "react";
import BreadCrumbComp from "../../components/breadCrumb";
import { Grid, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import SimpleSelect from "../../components/selects/simpleSelect";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { months } from "../../constants/selectData";
import FormTextField from "../../components/textField/formTextField";
import LaddaButton from "../../components/buttons/laddaButton";
import SimpleTable from "../../components/tables/simpleTable";
import { icons } from "../../constants";
import {
  callApiAsync,
  getSuppliersForeignTrade,
  getAnnualProjectionYears,
  getAnnualProjectionsByYear,
  createOrUpdateAnnualProjection,
  deleteAnnualProjection
} from "../../services/apiService";
import AddSupplier from "./modals/addSuppliers";
import VerificationModal from "../../components/modals/verificationModal";

const columns = [
  { field: "month_name", header: "Mes", align: "center", sortable: true },
  { field: "supplier_name", header: "Proveedor", align: "center", sortable: true },
  { field: "product", header: "Producto/Marca", align: "center", sortable: true },
  { field: "order_number", header: "Nro de Orden", align: "center" },
  {
    field: "actionButtons",
    header: "",
    columnType: "actionButtons",
    columnWidth: "120px"
  }
];

const currentYear = new Date().getFullYear();

const emptySelectOption = (label) => ({ name: label, value: "" });

const defaultProjectionFormValues = {
  month: "",
  supplier_id: "",
  product: "",
  order_number: ""
};

const AddAnnualProjectionModal = () => {
  const [seriesDataTable, setSeriesDataTable] = useState([]);
  const [addSuppliersModal, setAddSuppliersModal] = useState(false);
  const [selectData, setSelectData] = useState([]);
  const [yearsOptions, setYearsOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [editingId, setEditingId] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const isCurrentYear = selectedYear === currentYear;

  // Validaciones: todas obligatorias
  const validationSchema = yup.object().shape({
    month: yup
      .mixed()
      .required("El mes es obligatorio"),
    supplier_id: yup
      .mixed()
      .required("El proveedor es obligatorio"),
    product: yup
      .string()
      .trim()
      .required("El producto/marca es obligatorio"),
    order_number: yup
      .string()
      .trim()
      .required("El Nro. de Orden es obligatorio")
  });

  const getSelectSuppliers = () => {
    callApiAsync(
      () => getSuppliersForeignTrade(),
      (data) => setSelectData(data || [])
    );
  };

  const loadYears = () => {
    callApiAsync(
      () => getAnnualProjectionYears(),
      (data) => {
        const list = Array.isArray(data) ? data : [];
        setYearsOptions(
          list.map((y) => ({ name: String(y), value: Number(y) }))
        );
      }
    );
  };

  const loadProjections = () => {
    callApiAsync(
      () => getAnnualProjectionsByYear(selectedYear),
      (data) => {
        const list = Array.isArray(data) ? data : [];
        setSeriesDataTable(
          list.map((row) => ({
            ...row,
            month_name:
              months.find((m) => m.value === row.month)?.name ?? String(row.month)
          }))
        );
      }
    );
  };

  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: defaultProjectionFormValues
  });

  useEffect(() => {
    getSelectSuppliers();
    loadYears();
  }, []);

  useEffect(() => {
    loadProjections();
  }, [selectedYear]);

  const onSubmit = () => {
    if (!isCurrentYear) return;
    const values = getValues();
    const payload = {
      year: selectedYear,
      month: values.month?.value ?? values.month,
      supplier_id: values.supplier_id?.value ?? values.supplier_id,
      product: values.product,
      order_number: values.order_number
    };
    if (editingId) payload.id = editingId;
    callApiAsync(
      () => createOrUpdateAnnualProjection(payload),
      () => {
        setEditingId(null);
        reset(defaultProjectionFormValues);
        loadProjections();
        loadYears();
      }
    );
  };

  const handleEdit = (rowData) => {
    if (!isCurrentYear) return;
    setEditingId(rowData.id);
    reset({
      month: rowData.month,
      supplier_id: rowData.supplier_id,
      product: rowData.product || "",
      order_number: rowData.order_number || ""
    });
  };

  const handleDeleteRow = (row) => {
    setRowToDelete(row);
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setRowToDelete(null);
  };

  const confirmDelete = async () => {
    if (!rowToDelete?.id) return;
    await callApiAsync(
      () => deleteAnnualProjection(rowToDelete.id),
      () => {
        closeDeleteModal();
        loadProjections();
        loadYears();
      }
    );
  };

  return (
    <div>
      <BreadCrumbComp />
      <div className="searchTitle">
        <h1>CREAR PROYECCIÓN ANUAL</h1>
      </div>

      <div className="table-container">
        <h1 className="title-bio-details-chard vm-20">
          PROYECCIÓN ANUAL {selectedYear}
        </h1>
        <br className="br-white-space" />

        <Grid container spacing={3} sx={{ marginBottom: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <p className="create-label ml-0">Año:</p>
            <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="year-select-label">Año</InputLabel>
              <Select
                labelId="year-select-label"
                value={selectedYear}
                label="Año"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {(yearsOptions.length ? yearsOptions : [{ name: String(currentYear), value: currentYear }]).map(
                  (opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <p className="create-label ml-0">Mes:</p>
              <SimpleSelect
                name="month"
                control={control}
                options={[emptySelectOption("Seleccione mes"), ...months]}
                margin="0"
                error={errors.month}
                disabled={!isCurrentYear}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <p className="create-label ml-0">Proveedor:</p>
              <Grid container wrap="nowrap" alignItems="center" spacing={1}>
                <Grid item xs>
                  <SimpleSelect
                    name="supplier_id"
                    control={control}
                    options={[
                      emptySelectOption("Seleccione proveedor"),
                      ...selectData
                    ]}
                    margin="0"
                    error={errors.supplier_id}
                    disabled={!isCurrentYear}
                  />
                </Grid>
                <Grid item sx={{ flexShrink: 0, alignSelf: "flex-end", pb: 0.5 }}>
                  <LaddaButton
                    width="40px"
                    fontSize="12px"
                    height="40px"
                    variant="contained"
                    margin="0"
                    type="button"
                    onClick={() => setAddSuppliersModal(true)}
                    disabled={!isCurrentYear}
                  >
                    <img className="btn-icon" src={icons.plusIcon} alt="icon" />
                  </LaddaButton>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6}>
              <p className="create-label ml-0">Producto/Marca:</p>
              <FormTextField
                name="product"
                control={control}
                margin="0"
                error={errors.product}
                disabled={!isCurrentYear}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <p className="create-label ml-0">Nro. de Orden:</p>
              <FormTextField
                name="order_number"
                control={control}
                margin="0"
                error={errors.order_number}
                disabled={!isCurrentYear}
              />
            </Grid>

            <Grid item xs={12}>
              <div className="j-center white-space">
                <LaddaButton
                  width="200px"
                  fontSize="12px"
                  height="40px"
                  variant="contained"
                  type="submit"
                  disabled={!isCurrentYear}
                >
                  <img
                    className="btn-icon"
                    src={icons.plusIcon}
                    alt="icon"
                    style={{ marginRight: "10px" }}
                  />
                  <p className="button_text">
                    {editingId ? "Actualizar proyección" : "Agregar proyección"}
                  </p>
                </LaddaButton>
              </div>
            </Grid>
          </Grid>
        </form>

        {!isCurrentYear && (
          <p className="create-label ml-0" style={{ color: "#d32f2f", marginBottom: 16 }}>
            Solo se pueden crear o editar proyecciones del año actual ({currentYear}). Este año es solo consulta.
          </p>
        )}


        <div style={{ marginTop: "2rem" }}>
          <SimpleTable
            data={seriesDataTable}
            columns={columns}
            showTooltips={true}
            buttons={
              isCurrentYear
                ? [
                  {
                    type: "icon",
                    icon: icons.editBlue,
                    onClick: handleEdit
                  },
                  {
                    type: "icon",
                    icon: icons.redTrash,
                    onClick: handleDeleteRow
                  }
                ]
                : []
            }
          />
        </div>
      </div>

      <AddSupplier
        open={addSuppliersModal}
        close={() => setAddSuppliersModal(false)}
        supplierOptions={selectData}
        setSupplierOptions={getSelectSuppliers}
      />

      <VerificationModal
        open={openDeleteModal}
        handleClose={closeDeleteModal}
        alertText={
          <>
            <b>¿Estás seguro de que deseas eliminar esta proyección?</b>
      
          </>
        }
        onConfirm={confirmDelete}
        iconSeverity="danger"
        yesNo={true}
      />
    </div>
  );
};

export default AddAnnualProjectionModal;
